// @generated-imports
import { buildAbsoluteUrl, hideGameLoading, preflightRom, resetGameError, saveLastGame, setDeepLinkParam, showGameError, showGameLoading, showToast, updateContinueButton, updatePlayerInfo } from './app.js';
import { clearOpenSessionMarker, ensureSessionServices, onSessionEndExtras, onSessionPlayingExtras, stopSessionServices } from './autosave.js';
import { CONFIG, dom, state } from './core.js';
import { diagnoseLoadError } from './diagnostics.js';
import { LOCAL_CORE_LABELS } from './dragdrop.js';
import { announce } from './gamepad.js';
import { isLayerOpen, layerClosed } from './layers.js';
import { getBiosFile, getLocalRomFile } from './library.js';
import { smoothBehavior, tryPersistStorage } from './lite.js';
import { isRomCachedOffline } from './offline.js';
import { closePauseMenu, openPauseMenu } from './pausemenu.js';
import { endPlaySession, enterPlayMode, exitPlayMode, getSetting, setStateButtonsVisible, startPlaySession } from './platform.js';
import { addRecentGame, bumpPlayCount, renderRecentGames } from './prefs.js';
import { loadStateRecord, saveSessionState } from './states.js';
import { bumpCounter } from './stats.js';
// @generated-imports
/* ===========================================================================
   CONTROLADOR DE SESSÃO (v2.0)
   ---------------------------------------------------------------------------
   Uma sessão = uma tentativa de abrir um jogo. Cada uma tem identidade
   crescente; toda continuação após `await` verifica se ainda é a sessão
   corrente (cargas antigas nunca substituem uma mais nova). Máquina de
   estados: idle → loading → ready → awaiting-gesture → playing → paused →
   error → closing. Recentes, contador e tempo de jogo só em `playing`.
   Toda saída (desligar, trocar de jogo, Select+Start, fechar aba) passa por
   endSession(): autosave (quando suportado) → tempo → destruição do iframe.
   ======================================================================== */

const SESSION_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  AWAITING: 'awaiting-gesture',
  PLAYING: 'playing',
  PAUSED: 'paused',
  ERROR: 'error',
  CLOSING: 'closing',
};

let sessionSeq = 0;

function currentSession() {
  return state.session || null;
}

function isCurrentSession(session) {
  return Boolean(session && state.session === session && !session.cancelled);
}

function sessionIsPlaying() {
  const session = currentSession();
  return Boolean(session && (session.status === SESSION_STATUS.PLAYING || session.status === SESSION_STATUS.PAUSED));
}

function createSession(spec) {
  sessionSeq += 1;
  const game = spec.game || null;
  return {
    id: String(sessionSeq),
    kind: spec.kind,                           // 'remote' | 'local'
    game,
    file: spec.file || null,
    core: spec.core || (game && game.core) || CONFIG.emulatorCore,
    title: spec.title || (game && game.title) || (spec.file && spec.file.name) || 'Jogo',
    gameId: spec.gameId || (game && game.id) || '',
    options: spec.options || {},
    status: SESSION_STATUS.LOADING,
    iframe: null,
    listener: null,
    watchdog: 0,
    cancelled: false,
    wasPlaying: false,
    romUrl: '',
    capabilities: { state: false, thumbnail: false, playerOrder: false, pause: false, fastForward: false },
    menuOpen: false,
    fastForward: false,
    timeline: [],
    timelineBytes: 0,
    pending: new Map(),
    requestSeq: 0,
    stages: { created: Date.now() },
    stagesFromPlayer: {},
    pendingResume: spec.options && spec.options.resumeRecord ? spec.options.resumeRecord : null,
    pendingPlayerOrder: spec.options && spec.options.playerOrder ? spec.options.playerOrder : null,
    lastSavedAt: 0,
  };
}

function setSessionStatus(session, status) {
  session.status = status;
  if (status === SESSION_STATUS.PLAYING) session.wasPlaying = true;
  document.body.dataset.sessionStatus = status;
}

/* --- Ciclo de vida ------------------------------------------------------- */

/** Abre uma sessão (remota ou local). Encerra a anterior com autosave. */
async function startSession(spec) {
  await endSession('switch');

  const session = createSession(spec);
  state.session = session;
  state.selectedGame = session.kind === 'remote' ? session.game : null;
  prepareSessionUi(session);

  try {
    if (session.kind === 'remote') {
      session.romUrl = buildAbsoluteUrl(session.game.romUrl);
      const lastPreflight = state.preflightOk.get(session.romUrl) || 0;
      // ROM guardada para offline: o service worker serve do cache — sem preflight.
      const offlineRom = await isRomCachedOffline(session.romUrl);
      session.stages.offline = offlineRom;
      if (!isCurrentSession(session)) return session;
      if (CONFIG.romPreflightEnabled && !offlineRom && Date.now() - lastPreflight > CONFIG.romPreflightTtlMs) {
        dom.gameLoadingText.textContent = 'Validando disponibilidade e permissão de acesso da ROM…';
        session.stages.preflightStart = Date.now();
        await preflightRom(session.romUrl);
        session.stages.preflightEnd = Date.now();
        if (!isCurrentSession(session)) return session; // substituída durante o preflight
        state.preflightOk.set(session.romUrl, Date.now());
      }
    }
    // BIOS guardada no navegador para este core? Vai por handshake (File),
    // tanto para ROM local quanto para ROM do catálogo.
    session.bios = await getBiosFile(session.core).catch(() => null);
    if (!isCurrentSession(session)) return session;
    dom.gameLoadingText.textContent = `Inicializando core ${session.core} no EmulatorJS…`;
    await mountPlayer(session);
    if (!isCurrentSession(session)) return session;
    hideGameLoading();
    // Só promove quem ainda está carregando: se o player já anunciou
    // ready/start durante a montagem, não rebaixa `playing` para `ready`.
    if (session.status === SESSION_STATUS.LOADING) {
      setSessionStatus(session, session.options.noAutostart ? SESSION_STATUS.AWAITING : SESSION_STATUS.READY);
    }
    announce(`${session.title} pronto.`);
  } catch (error) {
    if (!isCurrentSession(session)) return session;
    failSession(session, error);
  }
  return session;
}

function prepareSessionUi(session) {
  if (session.kind === 'remote') {
    saveLastGame(session.gameId);
    setDeepLinkParam(session.gameId);
    updateContinueButton();
    updatePlayerInfo(session.game);
  } else {
    const systemLabel = LOCAL_CORE_LABELS[session.core] || session.core;
    dom.selectedGameTitle.textContent = session.title;
    dom.selectedGameMeta.textContent = `ROM local • ${systemLabel} • ${session.core}`;
  }
  resetGameError();
  showGameLoading(`Carregando ${session.title}.`);
  dom.playerPlaceholder.classList.add('is-hidden');
  dom.emulatorMount.classList.add('is-active');
  dom.powerOffBtn.classList.remove('is-hidden');
  setStateButtonsVisible(false);
  document.body.classList.add('is-playing');
  dom.playerSection.scrollIntoView({ behavior: smoothBehavior(), block: 'start' });
}

function buildPlayerUrl(session) {
  const playerUrl = new URL(CONFIG.playerPage, window.location.href);
  playerUrl.searchParams.set('session', session.id);
  // Identificador de build (allowlist compartilhada) — nunca URL livre.
  playerUrl.searchParams.set('build', CONFIG.emulatorBuild);
  playerUrl.searchParams.set('core', session.core);
  playerUrl.searchParams.set('title', session.title);
  if (session.kind === 'remote') {
    playerUrl.searchParams.set('rom', session.romUrl);
    if (session.bios) playerUrl.searchParams.set('handshake', '1');
  } else {
    playerUrl.searchParams.set('local', '1');
  }
  if (CONFIG.localCoreBios && CONFIG.localCoreBios[session.core]) {
    playerUrl.searchParams.set('bios', new URL(CONFIG.localCoreBios[session.core], window.location.href).href);
  }
  if (state.liteMode) playerUrl.searchParams.set('lite', '1');
  if (session.options.noAutostart) playerUrl.searchParams.set('autostart', '0');
  return playerUrl.toString();
}

/** Cria o iframe e espera o player ficar pronto (ready). */
function mountPlayer(session) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      fn(value);
    };
    session.resolveMount = (value) => settle(resolve, value);
    session.rejectMount = (error) => settle(reject, error);

    const iframe = document.createElement('iframe');
    iframe.className = 'emulator-iframe';
    iframe.title = `Emulador — ${session.title}`;
    iframe.allow = 'autoplay; fullscreen; gamepad; cross-origin-isolated';
    iframe.allowFullscreen = true;
    iframe.setAttribute('frameborder', '0');
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = '0';
    iframe.style.display = 'block';
    iframe.style.background = '#060711';
    iframe.onerror = () => session.rejectMount(new Error('Não foi possível carregar a página do player.'));

    session.listener = (event) => handleSessionMessage(session, event);
    window.addEventListener('message', session.listener);

    session.iframe = iframe;
    state.currentIframe = iframe; // compat: código legado consulta este campo
    dom.emulatorMount.innerHTML = '';
    dom.emulatorMount.appendChild(iframe);
    iframe.src = buildPlayerUrl(session);
    armSessionWatchdog(session);
  });
}

function armSessionWatchdog(session) {
  clearSessionWatchdog(session);
  session.watchdog = window.setTimeout(() => {
    if (!isCurrentSession(session)) return;
    if (session.status === SESSION_STATUS.LOADING && session.rejectMount) {
      session.rejectMount(new Error('Tempo limite ao preparar o emulador ou carregar a ROM.'));
    }
  }, CONFIG.loaderWatchdogMs);
}

function clearSessionWatchdog(session) {
  if (session && session.watchdog) {
    window.clearTimeout(session.watchdog);
    session.watchdog = 0;
  }
}

/* --- Mensagens do player (por sessão) ----------------------------------- */

function handleSessionMessage(session, event) {
  if (event.origin !== window.location.origin) return;
  if (!session.iframe || event.source !== session.iframe.contentWindow) return;
  const data = event.data || {};
  if (data.session !== session.id) return; // sessão antiga ou desconhecida
  if (data.stages) session.stagesFromPlayer = data.stages;

  switch (data.type) {
    case 'emutab:loader-ok':
      armSessionWatchdog(session);
      return;
    case 'emutab:awaiting-rom':
      armSessionWatchdog(session);
      if (session.kind === 'local') deliverLocalRom(session);
      else deliverRemoteConfig(session);
      return;
    case 'emutab:ready':
      clearSessionWatchdog(session);
      if (session.resolveMount) session.resolveMount();
      return;
    case 'emutab:start':
      onSessionPlaying(session);
      return;
    case 'emutab:menu-request':
      // Trava também do lado do portal: dois pedidos colados (toque + Home,
      // ou um repique) não podem abrir e fechar o menu de uma vez.
      if (Date.now() - (session.menuToggledAt || 0) < 500) return;
      session.menuToggledAt = Date.now();
      if (session.status === SESSION_STATUS.PLAYING) openPauseMenu();
      else if (session.status === SESSION_STATUS.PAUSED && isLayerOpen('pause')) closePauseMenu();
      return;
    case 'emutab:capabilities':
      session.capabilities = Object.assign({ state: false, thumbnail: false, playerOrder: false, pause: false, fastForward: false }, data.capabilities || {});
      onSessionCapabilities(session);
      return;
    case 'emutab:error':
      handleSessionError(session, data);
      return;
    case 'emutab:player-error':
      // Erros do runtime do emulador: só derrubam a sessão se ela ainda não
      // chegou a rodar; depois disso vão para o diagnóstico sem interromper.
      if (session.status === SESSION_STATUS.LOADING) handleSessionError(session, data);
      else session.lastPlayerError = data;
      return;
    case 'emutab:state-saved':
    case 'emutab:state-loaded':
    case 'emutab:player-order':
    case 'emutab:paused':
    case 'emutab:fast-forward':
      resolvePending(session, data);
      return;
    default:
      return;
  }
}

async function deliverLocalRom(session) {
  if (session.kind !== 'local') return;
  try {
    const file = session.file || await getLocalRomFile(session.gameId);
    if (!file) throw new Error('Arquivo da ROM local não encontrado.');
    if (!isCurrentSession(session)) return;
    session.file = file;
    postToSession(session, { type: 'emutab:load-rom', file, core: session.core, title: session.title, bios: session.bios || null });
  } catch (error) {
    if (session.rejectMount) session.rejectMount(error);
  }
}

/** ROM do catálogo com BIOS local: entrega a BIOS e libera o loader. */
function deliverRemoteConfig(session) {
  try {
    postToSession(session, { type: 'emutab:configure', bios: session.bios || null });
  } catch (error) {
    if (session.rejectMount) session.rejectMount(error);
  }
}

function handleSessionError(session, data) {
  const error = new Error(data.message || 'Erro reportado pelo player.');
  error.code = data.code || null;
  if (session.status === SESSION_STATUS.LOADING && session.rejectMount) {
    session.rejectMount(error);
    return;
  }
  if (isCurrentSession(session)) failSession(session, error);
}

function failSession(session, error) {
  clearSessionWatchdog(session);
  setSessionStatus(session, SESSION_STATUS.ERROR);
  const diag = diagnoseLoadError(session, error);
  console.error('[EmuTab] Sessão falhou:', diag.code, error);
  endPlaySession();
  exitPlayMode();
  onSessionEndExtras(session);
  detachSession(session);
  if (state.session === session) state.session = null;
  showGameError(diag.message, diag);
}

function onSessionPlaying(session) {
  if (!isCurrentSession(session)) return;
  hideGameLoading();
  setSessionStatus(session, SESSION_STATUS.PLAYING);
  session.startedPlayingAt = Date.now();
  // Só aqui a partida "conta": recentes, contador e tempo. (Antes contava
  // em `ready`, inclusive com o jogo parado esperando o Play.)
  const id = session.gameId;
  startPlaySession(id);
  bumpPlayCount(id);
  addRecentGame(id);
  renderRecentGames();
  tryPersistStorage();
  enterPlayMode();
  if (session.kind === 'local') {
    saveLastGame(id);
    updateContinueButton();
  }
  announce(`${session.title} iniciado.`);
  onSessionPlayingExtras(session);
  if (session.capabilities.state) applyPendingResume(session);
  if (session.capabilities.playerOrder) applyPendingPlayerOrder(session);
}

function onSessionCapabilities(session) {
  if (!isCurrentSession(session)) return;
  setStateButtonsVisible(session.capabilities.state);
  if (dom.pauseMenuBtn) dom.pauseMenuBtn.classList.remove('is-hidden');
  if (session.status === SESSION_STATUS.PLAYING) {
    ensureSessionServices(session);
    applyPendingResume(session);
    applyPendingPlayerOrder(session);
  }
}

async function applyPendingResume(session) {
  const record = session.pendingResume;
  if (!record || !session.capabilities.state) return;
  session.pendingResume = null;
  const ok = await loadStateRecord(session, record, { silent: false });
  if (!ok) showToast('Não deu para retomar o ponto salvo — o jogo começou do início.');
}

async function applyPendingPlayerOrder(session) {
  const order = session.pendingPlayerOrder;
  if (!order || !session.capabilities.playerOrder) return;
  session.pendingPlayerOrder = null;
  const ok = await setSessionPlayerOrder(session, order);
  showToast(ok ? `Controles atribuídos para ${order.length} jogadores.` : 'O emulador não aceitou a ordem dos controles — ajuste no menu dele.');
  if (ok && order.length >= 2) bumpCounter('multiSessions');
}

/** Encerra a sessão corrente: autosave → tempo → iframe. */
async function endSession(reason) {
  const session = state.session;
  if (!session) return null;
  if (session.status === SESSION_STATUS.CLOSING) return null;
  // Saída normal: a marca de "sessão aberta" some já (antes de qualquer await)
  // — se o pagehide interromper o resto, não haverá falso "travou".
  clearOpenSessionMarker();
  if (isLayerOpen('pause')) { dom.pauseMenu.classList.add('is-hidden'); layerClosed('pause'); }
  session.menuOpen = false;
  const wasPausedStatus = session.status === SESSION_STATUS.PAUSED;
  session.cancelled = true;
  setSessionStatus(session, SESSION_STATUS.CLOSING);
  clearSessionWatchdog(session);
  if (session.rejectMount) session.rejectMount(new Error('Sessão encerrada.'));

  let autosaved = null;
  const canAutosave = session.wasPlaying && session.capabilities.state
    && getSetting('autoSaveOnExit') && reason !== 'error';
  stopSessionServices(session);
  if (wasPausedStatus && session.pausedByPortal) {
    // o emulador precisa estar rodando para responder o getState
    try { postToSession(session, { type: 'emutab:pause', paused: false }); } catch (_) {}
  }
  if (canAutosave) {
    showGameLoading('Salvando seu progresso…');
    try {
      autosaved = await saveSessionState(session, { kind: 'auto', timeoutMs: 2500 });
    } catch (_) { autosaved = null; }
    hideGameLoading();
    if (!autosaved) showToast('Não deu para guardar o progresso desta sessão — o emulador não respondeu a tempo.');
  }

  endPlaySession();
  exitPlayMode();
  onSessionEndExtras(session);
  if (dom.pauseMenuBtn) dom.pauseMenuBtn.classList.add('is-hidden');
  rejectAllPending(session);
  detachSession(session);
  if (state.session === session) state.session = null;
  document.body.dataset.sessionStatus = SESSION_STATUS.IDLE;
  return autosaved;
}

function detachSession(session) {
  if (session.listener) {
    window.removeEventListener('message', session.listener);
    session.listener = null;
  }
  if (session.iframe) {
    try { session.iframe.src = 'about:blank'; } catch (_) {}
    if (session.iframe.parentNode) session.iframe.parentNode.removeChild(session.iframe);
    session.iframe = null;
  }
  if (state.currentIframe && (!state.session || state.session === session)) state.currentIframe = null;
  if (dom.emulatorMount) dom.emulatorMount.innerHTML = '';
}

/* --- Adaptador: pedidos com resposta ------------------------------------ */

function postToSession(session, payload) {
  if (!session || !session.iframe || !session.iframe.contentWindow) throw new Error('Sessão sem player.');
  session.iframe.contentWindow.postMessage(Object.assign({ session: session.id }, payload), window.location.origin);
}

function requestFromSession(session, payload, responseType, timeoutMs) {
  return new Promise((resolve) => {
    if (!session || !session.iframe) { resolve(null); return; }
    session.requestSeq += 1;
    const requestId = session.requestSeq;
    const timer = window.setTimeout(() => {
      if (session.pending.has(requestId)) {
        session.pending.delete(requestId);
        resolve(null);
      }
    }, timeoutMs || 6000);
    session.pending.set(requestId, { resolve, timer, responseType });
    try {
      postToSession(session, Object.assign({ requestId }, payload));
    } catch (_) {
      window.clearTimeout(timer);
      session.pending.delete(requestId);
      resolve(null);
    }
  });
}

function resolvePending(session, data) {
  const pending = session.pending.get(data.requestId);
  if (!pending) return;
  session.pending.delete(data.requestId);
  window.clearTimeout(pending.timer);
  pending.resolve(data);
}

function rejectAllPending(session) {
  session.pending.forEach((pending) => {
    window.clearTimeout(pending.timer);
    pending.resolve(null);
  });
  session.pending.clear();
}

function requestSessionState(session, timeoutMs) {
  return requestFromSession(session, { type: 'emutab:save-state' }, 'emutab:state-saved', timeoutMs)
    .then((data) => (data && data.ok && data.state ? data : null));
}

function sendSessionState(session, bytes, timeoutMs) {
  return requestFromSession(session, { type: 'emutab:load-state', state: bytes }, 'emutab:state-loaded', timeoutMs)
    .then((data) => Boolean(data && data.ok));
}

function setSessionPlayerOrder(session, order) {
  return requestFromSession(session, { type: 'emutab:set-player-order', order }, 'emutab:player-order', 4000)
    .then((data) => Boolean(data && data.ok));
}

// Fechar a aba no meio do jogo: encerra a sessão (tempo contabilizado; o
// autosave é melhor esforço — pagehide não espera promessas longas).
window.addEventListener('pagehide', () => { endSession('pagehide'); });

// @generated-exports
export { SESSION_STATUS, applyPendingPlayerOrder, applyPendingResume, armSessionWatchdog, buildPlayerUrl, clearSessionWatchdog, createSession, currentSession, deliverLocalRom, deliverRemoteConfig, detachSession, endSession, failSession, handleSessionError, handleSessionMessage, isCurrentSession, mountPlayer, onSessionCapabilities, onSessionPlaying, postToSession, prepareSessionUi, rejectAllPending, requestFromSession, requestSessionState, resolvePending, sendSessionState, sessionIsPlaying, sessionSeq, setSessionPlayerOrder, setSessionStatus, startSession };
// @generated-exports
